import { useState } from 'react';
import type { ExportDestinationContent, ExportTriggerPolicy, Surface } from '@capture/shared';
import { useFormConfigStore, resolveV3 } from '../state/store';
import { DestinationList } from './DestinationList';
import { DestinationDetail, JOB_DETAIL_TABS } from './DestinationDetail';
import { AddDestinationDialog } from './AddDestinationDialog';
import type { RoutingFieldSummary } from './RoutingPanel';
import styles from './export-composer.module.css';

/**
 * Job-Config "Export Destinations" tab (Plan 3 Task 5).
 *
 * Manages the job's export destinations at the JOB level — reads/writes the V3
 * top-level `content.exportDestinations` (NOT the legacy `content.export.
 * destinations`, and NOT per-form mappings). Field mapping is intentionally
 * absent here: it became per-form in Task 6, which is why the detail view gets
 * `JOB_DETAIL_TABS` (the full set minus `'mapping'`). Runtime errors moved to
 * the Export History log (F-036).
 */

interface DestinationListTabProps {
  jobTypeId: string;
  fields: RoutingFieldSummary[];
  /** Job-type processing mode forwarded to DestinationDetail for grouping constraints. */
  processingMode?: string;
  /** Job-type default trigger policy forwarded to DestinationDetail. */
  jobTypeDefaultPolicy?: ExportTriggerPolicy | null;
  /** Job type surface — forwarded to DestinationDetail for the transactions prereq notice. */
  surface?: Surface;
}

export function DestinationListTab({
  jobTypeId,
  fields,
  processingMode,
  jobTypeDefaultPolicy,
  surface,
}: DestinationListTabProps) {
  // Source of truth is the V3 top-level `exportDestinations`. Mirror the store's
  // `resolveV3` (as FieldListPane does) so both the production `contentV3` seed
  // and the test/pre-load `content` seed resolve correctly.
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const legacyContent = useFormConfigStore((s) => s.content);
  const addExportDestination = useFormConfigStore((s) => s.addExportDestination);
  const updateExportDestination = useFormConfigStore((s) => s.updateExportDestination);
  const deleteExportDestination = useFormConfigStore((s) => s.deleteExportDestination);

  const destinations = resolveV3({ contentV3, content: legacyContent })?.exportDestinations ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const selectedDest = destinations.find((d) => d.id === selectedId) ?? null;

  function handleAdd(dest: ExportDestinationContent) {
    // The store splits the destination across levels: the dest (minus mappings)
    // lands in V3 `exportDestinations`, and an empty mapping seed lands on the
    // active form. Reusing AddDestinationDialog here is correct because it always
    // produces an empty `mappings: []`, which the store strips out.
    addExportDestination(dest);
    setSelectedId(dest.id);
    setShowAdd(false);
  }

  function handleDelete(id: string) {
    deleteExportDestination(id);
    if (selectedId === id) setSelectedId(null);
  }

  return (
    <div className={styles.composer}>
      <div className={styles.list}>
        <DestinationList
          // V3 destinations carry no per-destination `mappings`; DestinationList
          // never reads that field (it shows name + adapter + format only).
          destinations={destinations as ExportDestinationContent[]}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onAdd={() => setShowAdd(true)}
        />
      </div>

      <div className={styles.detail}>
        {selectedDest ? (
          <DestinationDetail
            // Job-level destinations have no `mappings`; the mapping tab is
            // omitted, so `mappings` is never read. Supply `[]` to satisfy the
            // shared `ExportDestinationContent` prop type without loosening it.
            destination={{ ...selectedDest, mappings: [] }}
            fields={fields}
            jobTypeId={jobTypeId}
            processingMode={processingMode}
            jobTypeDefaultPolicy={jobTypeDefaultPolicy}
            surface={surface}
            tabs={JOB_DETAIL_TABS}
            onUpdate={(patch) => updateExportDestination(selectedDest.id, patch)}
            onDelete={() => handleDelete(selectedDest.id)}
          />
        ) : (
          <div className={styles.empty}>
            {destinations.length === 0 ? (
              <>
                <p>No export destinations configured</p>
                <button type="button" className={styles.emptyAddBtn} onClick={() => setShowAdd(true)}>
                  + Add destination
                </button>
              </>
            ) : (
              <p>Select a destination to configure it</p>
            )}
          </div>
        )}
      </div>

      {showAdd && (
        <AddDestinationDialog onAdd={handleAdd} onCancel={() => setShowAdd(false)} />
      )}
    </div>
  );
}
